// Resolver unico delle "Guide rapide" mostrate in cima a ogni sezione del CRM.
// - Admin: usa le guide di adminGuides.js (per rotta).
// - Stakeholder Hotel: RIUSA i testi curati di HOTEL_GUIDES (per pagina).
// - Stakeholder Ristorante: usa RESTAURANT_GUIDES (definite qui, concise).
// Tutte le guide vengono normalizzate al formato { title, intro, steps:[{h,p}], tip? }.

import { getAdminGuide } from './adminGuides';
import { HOTEL_GUIDES } from './hotelGuides';

// ---------------- Guide Ristorante (per slug di /area/restaurant/<slug>) ----------------
const RESTAURANT_GUIDES = {
  panoramica: {
    title: 'Panoramica',
    intro: 'La visione d\'insieme del tuo ristorante: prenotazioni, ordini e incassi del periodo.',
    steps: [
      { h: 'Leggi i numeri chiave', p: 'Coperti, prenotazioni e incassi ti dicono subito come sta andando la giornata.' },
      { h: 'Controlla i prossimi arrivi', p: 'Verifica le prenotazioni imminenti e preparati per il servizio.' },
      { h: 'Vai dove serve', p: 'Dai riquadri salti direttamente alla sezione utile (Prenotazioni, Menu, Cassa).' },
    ],
  },
  vetrina: {
    title: 'Vetrina',
    intro: 'La pagina pubblica del tuo ristorante su /dove-mangiare: foto, descrizione e informazioni.',
    steps: [
      { h: 'Cura la presentazione', p: 'Aggiungi descrizione, foto (dalla Galleria) e specialità per invogliare i clienti.' },
      { h: 'Pubblica', p: 'Attiva la vetrina per comparire nell\'elenco pubblico dei ristoranti.' },
      { h: 'Verifica i dati', p: 'Contatti, orari e posizione si aggiornano dalle Impostazioni.' },
    ],
  },
  piantina: {
    title: 'Piantina Sala',
    intro: 'La mappa dei tavoli: disponi sale e tavoli come nella realtà.',
    steps: [
      { h: 'Crea le sale', p: 'Aggiungi una o più sale (interno, dehors) e posiziona i tavoli.' },
      { h: 'Imposta i coperti', p: 'Definisci i posti di ogni tavolo: servono per le prenotazioni.' },
      { h: 'Usa in servizio', p: 'La piantina guida l\'assegnazione dei tavoli e la gestione della sala.' },
    ],
  },
  camerieri: {
    title: 'Camerieri',
    intro: 'Gestisci il personale di sala e i loro accessi all\'app cameriere.',
    steps: [
      { h: 'Aggiungi i camerieri', p: 'Crea un profilo con credenziali dedicate per ogni cameriere.' },
      { h: 'Assegna i permessi', p: 'Decidi cosa può fare ciascuno: prendere ordini, gestire tavoli, incassare.' },
      { h: 'Mance e turni', p: 'Le mance per cameriere e i turni si consultano in Cassa e in Orari e Turni.' },
    ],
  },
  rider: {
    title: 'Rider',
    intro: 'Gestisci i rider per le consegne a domicilio e i loro accessi all\'app.',
    steps: [
      { h: 'Registra i rider', p: 'Crea un profilo con credenziali dedicate per ogni rider.' },
      { h: 'Affida le consegne', p: 'Gli ordini in consegna vengono assegnati ai rider disponibili.' },
      { h: 'Monitora', p: 'Segui lo stato delle consegne in tempo reale.' },
    ],
  },
  prenotazioni: {
    title: 'Prenotazioni',
    intro: 'Tutte le prenotazioni tavolo del tuo ristorante, da confermare e gestire.',
    steps: [
      { h: 'Ricevi e conferma', p: 'Le nuove prenotazioni arrivano in tempo reale: confermale o gestiscile.' },
      { h: 'Assegna il tavolo', p: 'Collega la prenotazione a un tavolo della piantina.' },
      { h: 'Caparra (se attiva)', p: 'Se hai impostato una caparra, viene richiesta al cliente al momento della prenotazione.' },
    ],
  },
  recensioni: {
    title: 'Recensioni',
    intro: 'Le recensioni dei clienti sul tuo ristorante e sul servizio.',
    steps: [
      { h: 'Leggi i giudizi', p: 'Vedi voti e commenti lasciati dai clienti dopo la visita.' },
      { h: 'Migliora', p: 'Usa i feedback per correggere ciò che non va e valorizzare i punti di forza.' },
    ],
  },
  'orari-turni': {
    title: 'Orari e Turni',
    intro: 'Definisci gli orari di apertura e i turni di servizio (pranzo, cena).',
    steps: [
      { h: 'Imposta gli orari', p: 'Indica giorni e fasce di apertura: regolano prenotazioni e ordini.' },
      { h: 'Crea i turni', p: 'Organizza il servizio in turni per gestire meglio la sala.' },
      { h: 'Chiusure', p: 'Aggiungi chiusure straordinarie per bloccare le prenotazioni in quei giorni.' },
    ],
  },
  pagamenti: {
    title: 'Cassa',
    intro: 'La cassa del ristorante: transazioni, depositi, rimborsi e report incassi.',
    steps: [
      { h: 'Consulta le transazioni', p: 'Vedi tutti i pagamenti ricevuti, con dettaglio e stato.' },
      { h: 'Rimborsi e depositi', p: 'Emetti rimborsi tracciati e collega i depositi alle prenotazioni.' },
      { h: 'Report e mance', p: 'Analizza gli incassi giorno per giorno, esporta i dati e controlla le mance per cameriere.' },
    ],
    tip: 'Inserisci le chiavi Stripe/Satispay nel tab Provider: finché non lo fai, i pagamenti restano simulati (demo).',
  },
  'carta-digitale': {
    title: 'Carta Digitale',
    intro: 'Il menu digitale che i clienti aprono dal QR al tavolo.',
    steps: [
      { h: 'Attiva la carta', p: 'Rendi il menu consultabile da smartphone tramite QR.' },
      { h: 'Collega i contenuti', p: 'La carta mostra i piatti e i vini che gestisci nelle relative sezioni.' },
      { h: 'Pagamento al tavolo', p: 'Se attivo, il cliente può anche pagare il conto direttamente dal QR.' },
    ],
  },
  menu: {
    title: 'Menu',
    intro: 'Costruisci il menu del ristorante: categorie, piatti, prezzi e allergeni.',
    steps: [
      { h: 'Crea le categorie', p: 'Organizza il menu in sezioni (antipasti, primi, dolci…).' },
      { h: 'Aggiungi i piatti', p: 'Per ogni piatto imposta nome, descrizione, prezzo, foto e disponibilità.' },
      { h: 'Allergeni', p: 'Nel tab Allergeni indichi gli allergeni di ciascun piatto: appaiono nella carta digitale.' },
    ],
  },
  vini: {
    title: 'Carta dei Vini',
    intro: 'La carta dei vini: cantine, etichette e disponibilità.',
    steps: [
      { h: 'Aggiungi le cantine', p: 'Registra i produttori/cantine dei tuoi vini.' },
      { h: 'Inserisci le etichette', p: 'Per ogni vino imposta annata, prezzo e disponibilità.' },
      { h: 'Filtro disponibilità', p: 'I vini esauriti si nascondono in automatico dalla carta mostrata al cliente.' },
    ],
  },
  personalizzazione: {
    title: 'Personalizzazione',
    intro: 'Personalizza l\'aspetto del tuo ristorante (logo, colori, stile) coerente col brand.',
    steps: [
      { h: 'Imposta il brand', p: 'Carica il logo e scegli i colori: si riflettono su vetrina e carta digitale.' },
      { h: 'Anteprima', p: 'Controlla il risultato prima di salvare.' },
    ],
  },
  'canali-vendita': {
    title: 'Canali di Vendita',
    intro: 'Gestisci i canali da cui ricevi ordini: sala, asporto e delivery.',
    steps: [
      { h: 'Attiva i canali', p: 'Abilita asporto e/o consegna a domicilio oltre al servizio in sala.' },
      { h: 'Regole per canale', p: 'Imposta orari e condizioni specifiche per ciascun canale.' },
    ],
  },
  'delivery-hubrise': {
    title: 'Delivery & HubRise',
    intro: 'Configura le consegne a domicilio e l\'integrazione HubRise per sincronizzare gli ordini.',
    steps: [
      { h: 'Configura il delivery', p: 'Imposta zone di consegna, costi e tempi.' },
      { h: 'Collega HubRise', p: 'Con le credenziali HubRise sincronizzi gli ordini dai marketplace.' },
    ],
    tip: 'Senza credenziali, l\'integrazione resta in modalità demo.',
  },
  'qr-code': {
    title: 'QR Code',
    intro: 'Genera i QR code dei tavoli per menu e ordinazioni.',
    steps: [
      { h: 'Genera i QR', p: 'Crea un QR per ogni tavolo: il cliente lo scansiona per aprire la carta.' },
      { h: 'Stampa ed esponi', p: 'Scarica i QR e posizionali sui tavoli.' },
      { h: 'Monitora le scansioni', p: 'Vedi quante volte i QR vengono usati.' },
    ],
  },
  'qr-personalizzazione': {
    title: 'QR Personalizzazione',
    intro: 'Personalizza l\'aspetto grafico dei QR code (colori, logo).',
    steps: [
      { h: 'Stile del QR', p: 'Adatta colori e logo al tuo brand mantenendo il QR leggibile.' },
      { h: 'Anteprima', p: 'Verifica che resti scansionabile prima di generarlo.' },
    ],
  },
  logistica: {
    title: 'Logistica',
    intro: 'Gestisci la logistica degli ordini: preparazione, imballaggi e consegne.',
    steps: [
      { h: 'Organizza la preparazione', p: 'Tieni sotto controllo lo stato degli ordini in lavorazione.' },
      { h: 'Imballaggi e consegne', p: 'Coordina imballaggi e affidamento ai rider.' },
    ],
  },
  moduli: {
    title: 'Moduli',
    intro: 'Attiva o disattiva le funzioni del ristorante con semplici interruttori.',
    steps: [
      { h: 'Accendi ciò che ti serve', p: 'Ogni interruttore abilita una funzione (pagamenti, reclami, customer care…).' },
      { h: 'Semplifica', p: 'Disattiva i moduli che non usi per un gestionale più pulito.' },
    ],
  },
  'customer-care': {
    title: 'Customer Care',
    intro: 'Lo spazio per assistere i clienti e gestire le loro richieste.',
    steps: [
      { h: 'Ricevi le richieste', p: 'Le domande dei clienti arrivano qui per essere gestite.' },
      { h: 'Rispondi e chiudi', p: 'Tieni traccia delle conversazioni e chiudi le richieste risolte.' },
    ],
  },
  messaggi: {
    title: 'Messaggi',
    intro: 'La messaggistica con i clienti collegata a prenotazioni e ordini.',
    steps: [
      { h: 'Tutto in un posto', p: 'I messaggi col cliente restano collegati alla sua prenotazione o al suo ordine.' },
      { h: 'Rispondi in fretta', p: 'Una risposta rapida migliora l\'esperienza e riduce i no-show.' },
    ],
  },
  email: {
    title: 'Email',
    intro: 'Personalizza le email automatiche del ristorante (conferme, promemoria).',
    steps: [
      { h: 'Scegli il template', p: 'Modifica i testi delle email inviate ai clienti.' },
      { h: 'Segnaposto', p: 'Usa i token (nome, data, coperti): vengono sostituiti con i dati reali.' },
    ],
    tip: 'L\'invio reale richiede Postmark configurato; altrimenti resta in modalità demo.',
  },
  reclami: {
    title: 'Reclami',
    intro: 'Gestisci i reclami dei clienti in modo tracciato, con rimborsi sicuri.',
    steps: [
      { h: 'Apri il reclamo', p: 'Ogni reclamo ha un thread col cliente tramite link di tracciamento.' },
      { h: 'Gestisci e rispondi', p: 'Segui la conversazione e proponi una soluzione.' },
      { h: 'Rimborso sicuro', p: 'Gli eventuali rimborsi sono idempotenti (nessun doppio addebito) e tracciati.' },
    ],
  },
  impostazioni: {
    title: 'Impostazioni',
    intro: 'Il centro di controllo del ristorante: dati, prenotazioni, caparra e integrazioni.',
    steps: [
      { h: 'Dati del ristorante', p: 'Nome, contatti, indirizzo e orari usati in tutto il gestionale.' },
      { h: 'Regole prenotazioni', p: 'Imposta modalità e caparra (fissa o percentuale) per le prenotazioni.' },
      { h: 'Integrazioni', p: 'Colleghi pagamenti e servizi esterni quando disponibili.' },
    ],
  },
};

// ---------------- Dashboard per tenant + strumenti condivisi ----------------
const HOTEL_DASHBOARD = {
  title: 'Dashboard Hotel',
  intro: 'La panoramica della tua struttura: prenotazioni, occupazione e ricavi del periodo.',
  steps: [
    { h: 'Leggi i numeri chiave', p: 'Prenotazioni, occupazione e ricavi ti danno subito il polso della struttura.' },
    { h: 'Prossimi arrivi', p: 'Controlla check-in e check-out imminenti per organizzare l\'accoglienza.' },
    { h: 'Vai dove serve', p: 'Dai riquadri raggiungi rapidamente Camere, Calendario e Prenotazioni.' },
  ],
};

const RESTAURANT_DASHBOARD = RESTAURANT_GUIDES.panoramica;

const SHARED = {
  gallery: {
    title: 'Galleria',
    intro: 'L\'archivio immagini della tua area: carica una volta, usa ovunque.',
    steps: [
      { h: 'Carica le foto', p: 'Le immagini vengono ottimizzate in automatico (AVIF + variante mobile) per pagine più veloci.' },
      { h: 'Riutilizza', p: 'Selezioni queste immagini da vetrina, camere/piatti e altre sezioni.' },
      { h: 'Tieni pulito', p: 'Rimuovi i doppioni per ritrovare tutto più facilmente.' },
    ],
  },
  email: {
    title: 'Email',
    intro: 'Le email della tua area: personalizza i testi inviati ai clienti.',
    steps: [
      { h: 'Modifica i template', p: 'Adatta i testi al tuo tono di voce.' },
      { h: 'Segnaposto', p: 'Usa i token che vengono sostituiti con i dati reali del cliente.' },
    ],
  },
  contatti: {
    title: 'Contatti',
    intro: 'I recapiti pubblici della tua area, mostrati ai clienti.',
    steps: [
      { h: 'Aggiorna i recapiti', p: 'Telefono, email e social devono essere sempre corretti.' },
      { h: 'Coerenza', p: 'Questi dati appaiono sulle pagine pubbliche collegate alla tua area.' },
    ],
  },
  sicurezza: {
    title: 'Sicurezza',
    intro: 'Proteggi il tuo account con passkey e gestione dei dispositivi.',
    steps: [
      { h: 'Passkey', p: 'Registra una passkey per un accesso più veloce e sicuro.' },
      { h: 'Dispositivi', p: 'Controlla e revoca i dispositivi che non riconosci.' },
    ],
  },
};

// ---------------- Esperienze (tenant_type = experience) ----------------
const EXPERIENCE_DASHBOARD = {
  title: 'Dashboard',
  intro: 'La panoramica delle tue esperienze: vendite, prossime sessioni e presenze.',
  steps: [
    { h: 'Leggi i numeri chiave', p: 'Biglietti venduti, incassi e prossime sessioni ti danno subito il quadro.' },
    { h: 'Controlla le sessioni', p: 'Verifica le date imminenti e i posti ancora disponibili.' },
    { h: 'Vai dove serve', p: 'Dai riquadri raggiungi Esperienze, Iscrizioni e Check-in.' },
  ],
};
const EXPERIENCE_GUIDES = {
  esperienze: {
    title: 'Esperienze',
    intro: 'Crea e gestisci le tue esperienze: tipologia, sessioni, biglietti e prezzi.',
    steps: [
      { h: 'Crea l\'esperienza', p: 'Scegli il tipo (evento singolo, di gruppo o corso) e compila descrizione e foto.' },
      { h: 'Sessioni e biglietti', p: 'Aggiungi date/sessioni e le tipologie di biglietto con i relativi prezzi.' },
      { h: 'Pubblica', p: 'Quando è completa, pubblicala: comparirà nel catalogo pubblico.' },
    ],
  },
  vetrina: {
    title: 'Vetrina',
    intro: 'La pagina pubblica delle tue esperienze nel catalogo.',
    steps: [
      { h: 'Cura la presentazione', p: 'Titolo, foto e descrizione chiara aumentano le conversioni.' },
      { h: 'Pubblica', p: 'Attiva la vetrina per comparire nel catalogo pubblico.' },
    ],
  },
  iscrizioni: {
    title: 'Iscrizioni',
    intro: 'Gli acquisti e le prenotazioni delle tue esperienze, con biglietti e QR.',
    steps: [
      { h: 'Consulta le iscrizioni', p: 'Vedi chi ha acquistato, per quale sessione e con quale biglietto.' },
      { h: 'Gestisci', p: 'Accedi ai biglietti, ai QR e agli eventuali annullamenti/rimborsi.' },
    ],
  },
  'check-in': {
    title: 'Check-in',
    intro: 'Valida i biglietti all\'ingresso scansionando il QR.',
    steps: [
      { h: 'Scansiona il QR', p: 'Inquadra il QR del biglietto: il sistema conferma la validità.' },
      { h: 'Evita i doppioni', p: 'Un biglietto già usato viene segnalato: niente ingressi doppi.' },
    ],
  },
  analytics: {
    title: 'Analytics',
    intro: 'Andamento di vendite e presenze delle tue esperienze.',
    steps: [
      { h: 'Scegli il periodo', p: 'Confronta intervalli diversi per capire i trend.' },
      { h: 'Leggi i grafici', p: 'Incassi e presenze ti dicono cosa funziona di più.' },
    ],
  },
};

// ---------------- Tour dei Borghi (tenant_type = tour, stesse rotte /area/experience) ----------------
const TOUR_DASHBOARD = {
  title: 'Dashboard',
  intro: 'La panoramica dei tuoi tour: prenotazioni, prossime partenze e presenze.',
  steps: [
    { h: 'Leggi i numeri chiave', p: 'Prenotazioni, incassi e prossime partenze in un colpo d\'occhio.' },
    { h: 'Controlla le partenze', p: 'Verifica le date imminenti e i posti disponibili.' },
    { h: 'Vai dove serve', p: 'Dai riquadri raggiungi I miei Tour, Prenotazioni e Check-in.' },
  ],
};
const TOUR_GUIDES = {
  esperienze: {
    title: 'I miei Tour',
    intro: 'Crea e gestisci i tuoi tour: itinerario, date, posti e prezzi.',
    steps: [
      { h: 'Crea il tour', p: 'Imposta descrizione, foto e programma dell\'itinerario.' },
      { h: 'Date e posti', p: 'Aggiungi le partenze con posti disponibili e prezzi.' },
      { h: 'Pubblica', p: 'Quando è pronto, pubblicalo: comparirà nel catalogo pubblico.' },
    ],
  },
  vetrina: {
    title: 'Vetrina',
    intro: 'La pagina pubblica dei tuoi tour nel catalogo.',
    steps: [
      { h: 'Cura la presentazione', p: 'Foto e descrizione dell\'itinerario invogliano alla prenotazione.' },
      { h: 'Pubblica', p: 'Attiva la vetrina per comparire nel catalogo pubblico.' },
    ],
  },
  iscrizioni: {
    title: 'Prenotazioni',
    intro: 'Le prenotazioni dei tuoi tour, con biglietti e QR.',
    steps: [
      { h: 'Consulta le prenotazioni', p: 'Vedi chi ha prenotato, per quale partenza e con quale biglietto.' },
      { h: 'Gestisci', p: 'Accedi a biglietti, QR ed eventuali annullamenti/rimborsi.' },
    ],
  },
  'check-in': {
    title: 'Check-in',
    intro: 'Valida i biglietti dei partecipanti scansionando il QR alla partenza.',
    steps: [
      { h: 'Scansiona il QR', p: 'Inquadra il QR: il sistema conferma la validità del biglietto.' },
      { h: 'Evita i doppioni', p: 'Un biglietto già usato viene segnalato.' },
    ],
  },
  analytics: {
    title: 'Analytics',
    intro: 'Andamento di prenotazioni e presenze dei tuoi tour.',
    steps: [
      { h: 'Scegli il periodo', p: 'Confronta intervalli diversi per leggere i trend.' },
      { h: 'Leggi i grafici', p: 'Capisci quali tour vanno meglio.' },
    ],
  },
};

// ---------------- Guida del Parco (tenant_type = guida) ----------------
const GUIDA_ITINERARI = {
  title: 'I miei Itinerari',
  intro: 'Gestisci il tuo profilo di guida e gli itinerari che proponi nel Parco.',
  steps: [
    { h: 'Completa il profilo', p: 'Foto, bio, lingue e specializzazioni: compaiono in prenotazione e sui biglietti.' },
    { h: 'Crea gli itinerari', p: 'Imposta traccia, punti d\'interesse, difficoltà, stagione e prezzo.' },
    { h: 'Invia per approvazione', p: 'Gli itinerari vengono pubblicati dopo l\'approvazione dell\'admin.' },
  ],
  tip: 'Ogni modifica al profilo torna "in attesa": la versione pubblica resta visibile finché l\'admin non approva.',
};

// ---------------- E-commerce (tenant_type = ecommerce) ----------------
const ECOMMERCE_DASHBOARD = {
  title: 'Dashboard',
  intro: 'La panoramica del tuo negozio: ordini, incassi e prodotti.',
  steps: [
    { h: 'Leggi i numeri chiave', p: 'Ordini, incassi e prodotti attivi ti danno subito il quadro.' },
    { h: 'Ordini da evadere', p: 'Controlla gli ordini recenti e le richieste in attesa.' },
    { h: 'Vai dove serve', p: 'Dai riquadri raggiungi Prodotti, Ordini e Spedizioni.' },
  ],
};
const ECOMMERCE_GUIDES = {
  prodotti: {
    title: 'Prodotti',
    intro: 'Il catalogo del negozio: schede, prezzi, varianti e foto.',
    steps: [
      { h: 'Crea un prodotto', p: 'Compila nome, descrizione, prezzo e foto (dalla Galleria).' },
      { h: 'Varianti', p: 'Aggiungi taglie/colori con eventuali prezzi e giacenze dedicate.' },
      { h: 'Pubblica', p: 'Rendi il prodotto visibile nella vetrina del negozio.' },
    ],
  },
  inventario: {
    title: 'Inventario',
    intro: 'Le giacenze di magazzino e i movimenti di stock.',
    steps: [
      { h: 'Controlla le giacenze', p: 'Vedi quanti pezzi hai per ogni prodotto/variante.' },
      { h: 'Aggiorna lo stock', p: 'Registra carichi e scarichi: gli ordini scalano in automatico.' },
    ],
  },
  ordini: {
    title: 'Ordini',
    intro: 'Gli ordini dei clienti da gestire ed evadere.',
    steps: [
      { h: 'Elabora l\'ordine', p: 'Verifica pagamento e disponibilità, poi prepara la merce.' },
      { h: 'Aggiorna lo stato', p: 'Segna l\'ordine come spedito/consegnato per tenere informato il cliente.' },
    ],
  },
  clienti: {
    title: 'Clienti e Preventivi',
    intro: 'L\'anagrafica dei clienti e i preventivi emessi.',
    steps: [
      { h: 'Gestisci i clienti', p: 'Consulta lo storico ordini e i dati di contatto.' },
      { h: 'Crea preventivi', p: 'Prepara offerte personalizzate da trasformare in ordine.' },
    ],
  },
  spedizioni: {
    title: 'Spedizioni e Resi',
    intro: 'Gestisci spedizioni, tracciamenti e richieste di reso.',
    steps: [
      { h: 'Spedisci', p: 'Genera la spedizione e comunica il tracking al cliente.' },
      { h: 'Gestisci i resi', p: 'Accetta o rifiuta i resi e registra il rientro merce.' },
    ],
  },
  pagamenti: {
    title: 'Pagamenti e Fatture',
    intro: 'Transazioni e documenti fiscali del negozio.',
    steps: [
      { h: 'Consulta le transazioni', p: 'Vedi incassi e stato dei pagamenti.' },
      { h: 'Fatture', p: 'Genera e scarica i documenti fiscali degli ordini.' },
    ],
  },
  tasse: {
    title: 'Tasse e Profitto',
    intro: 'Impostazioni fiscali e calcolo del profitto.',
    steps: [
      { h: 'Configura le tasse', p: 'Imposta le aliquote applicate ai prodotti.' },
      { h: 'Leggi il profitto', p: 'Il netto è calcolato al netto di tasse e commissioni.' },
    ],
  },
  promozioni: {
    title: 'Promozioni',
    intro: 'Sconti e codici promozionali per aumentare le vendite.',
    steps: [
      { h: 'Crea una promo', p: 'Imposta sconto, durata e condizioni (es. spesa minima).' },
      { h: 'Codici sconto', p: 'Genera codici da condividere con i clienti.' },
    ],
  },
  recensioni: {
    title: 'Recensioni',
    intro: 'Le recensioni lasciate sui tuoi prodotti.',
    steps: [
      { h: 'Leggi i giudizi', p: 'Monitora voti e commenti dei clienti.' },
      { h: 'Migliora', p: 'Usa i feedback per schede prodotto e servizio.' },
    ],
  },
  vetrina: {
    title: 'Vetrina',
    intro: 'La vetrina pubblica del tuo negozio.',
    steps: [
      { h: 'Cura la vetrina', p: 'Organizza prodotti in evidenza, banner e categorie.' },
      { h: 'Pubblica', p: 'Rendi il negozio visibile ai clienti.' },
    ],
  },
  impostazioni: {
    title: 'Impostazioni e Integrazioni',
    intro: 'Configurazione del negozio e collegamento ai servizi esterni.',
    steps: [
      { h: 'Dati del negozio', p: 'Nome, contatti e parametri generali.' },
      { h: 'Integrazioni', p: 'Collega pagamenti e spedizioni quando disponibili.' },
    ],
  },
};

// ---------------- Area Staff (role = staff, rotte /staff-area/*) ----------------
const STAFF_GUIDES = {
  '/staff-area': {
    title: 'Area Staff',
    intro: 'La tua area personale come membro dello staff.',
    steps: [
      { h: 'Il tuo spazio', p: 'Da qui accedi a profilo, sicurezza e verifica dell\'identità.' },
      { h: 'Tieni tutto aggiornato', p: 'Completa i tuoi dati per essere operativo al più presto.' },
    ],
  },
  '/staff-area/profilo': {
    title: 'Profilo',
    intro: 'I tuoi dati personali e le informazioni del profilo.',
    steps: [
      { h: 'Aggiorna i dati', p: 'Mantieni corretti nome, contatti e informazioni personali.' },
      { h: 'Foto', p: 'Aggiungi una foto profilo per farti riconoscere.' },
    ],
  },
  '/staff-area/sicurezza': {
    title: 'Sicurezza',
    intro: 'Proteggi il tuo account con passkey e gestione dei dispositivi.',
    steps: [
      { h: 'Passkey', p: 'Registra una passkey per accessi più veloci e sicuri.' },
      { h: 'Dispositivi', p: 'Controlla e revoca i dispositivi che non riconosci.' },
    ],
  },
  '/staff-area/verifica': {
    title: 'Verifica Identità',
    intro: 'Verifica la tua identità caricando un documento.',
    steps: [
      { h: 'Carica il documento', p: 'Segui le istruzioni per completare la verifica.' },
      { h: 'Attendi l\'esito', p: 'Una volta verificato, sblocchi le funzioni riservate.' },
    ],
  },
  '/staff-area/incarichi': {
    title: 'Incarichi',
    intro: 'Qui troverai gli incarichi a te assegnati.',
    steps: [
      { h: 'In arrivo', p: 'Questa sezione sarà presto attiva: potrai vedere e gestire i tuoi incarichi.' },
    ],
  },
  '/staff-area/calendario': {
    title: 'Calendario',
    intro: 'Il calendario dei tuoi impegni.',
    steps: [
      { h: 'In arrivo', p: 'Questa sezione sarà presto attiva: pianificherai qui le tue attività.' },
    ],
  },
  '/staff-area/pagamenti': {
    title: 'Pagamenti',
    intro: 'I tuoi compensi e pagamenti.',
    steps: [
      { h: 'In arrivo', p: 'Questa sezione sarà presto attiva: consulterai qui i tuoi pagamenti.' },
    ],
  },
};

// ---------------- Modulo Eventi (trasversale: hotel/restaurant/experience/tour/ecommerce) ----------------
const EVENTS_GUIDE = {
  title: 'Eventi',
  intro: 'Crea e vendi biglietti per i tuoi eventi (festival, sagre, serate speciali) sulla vetrina pubblica di Sila Experiences.',
  steps: [
    { h: 'Attiva il modulo', p: 'Al primo accesso clicca \u201cAttiva Evento\u201d: la richiesta viene approvata dall\u2019amministratore.' },
    { h: 'Crea l\u2019evento', p: 'Imposta titolo, categoria, copertina (dalla Galleria), luogo e descrizione. Aggiungi date e tipi di biglietto con i prezzi.' },
    { h: 'Pubblica in vetrina', p: 'Quando \u00e8 completo, premi \u201cPubblica\u201d: comparir\u00e0 su /eventi con calendario e acquisto biglietti.' },
  ],
  tip: 'Il prezzo viene sempre ricalcolato lato server al checkout: nessun rischio di manomissione. I pagamenti sono in modalit\u00e0 demo finch\u00e9 non colleghi Stripe.',
};

function normalizePath(pathname) {
  if (!pathname) return '';
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

// Porta ogni guida al formato standard e promuove eventuale "Consiglio/Nota" finale a tip.
function normalize(g) {
  if (!g) return null;
  let steps = (g.steps || g.sections || []).slice();
  let tip = g.tip;
  if (!tip && steps.length) {
    const last = steps[steps.length - 1];
    if (last && /^(consiglio|nota|suggerimento)$/i.test((last.h || '').trim())) {
      tip = last.p;
      steps = steps.slice(0, -1);
    }
  }
  return { title: g.title, intro: g.intro, steps, tip };
}

function stakeholderGuide(tenantType, path) {
  // Modulo Eventi (trasversale a pi\u00f9 tenant)
  if (path === '/area/eventi') return EVENTS_GUIDE;

  // Strumenti condivisi
  if (path === '/area/gallery') return SHARED.gallery;
  if (path === '/area/email') return SHARED.email;
  if (path === '/area/contatti') return SHARED.contatti;
  if (path === '/area/sicurezza') return SHARED.sicurezza;

  // Hotel
  if (path === '/area' && tenantType === 'hotel') return HOTEL_DASHBOARD;
  if (path.startsWith('/area/hotel')) {
    const slug = path.replace('/area/hotel', '').replace(/^\//, '');
    return HOTEL_GUIDES[slug] || HOTEL_DASHBOARD;
  }

  // Ristorante
  if (path === '/area' && tenantType === 'restaurant') return RESTAURANT_DASHBOARD;
  if (path === '/area/restaurant') return RESTAURANT_GUIDES.panoramica;
  if (path.startsWith('/area/restaurant')) {
    const slug = path.replace('/area/restaurant', '').replace(/^\//, '');
    return RESTAURANT_GUIDES[slug] || RESTAURANT_GUIDES.panoramica;
  }

  // Esperienze / Tour (condividono le rotte /area/experience/*)
  if (path === '/area/experience' || path.startsWith('/area/experience/')) {
    const set = tenantType === 'tour' ? TOUR_GUIDES : EXPERIENCE_GUIDES;
    const dash = tenantType === 'tour' ? TOUR_DASHBOARD : EXPERIENCE_DASHBOARD;
    if (path === '/area/experience') return dash;
    const slug = path.replace('/area/experience/', '');
    return set[slug] || dash;
  }
  if (path === '/area' && tenantType === 'experience') return EXPERIENCE_DASHBOARD;
  if (path === '/area' && tenantType === 'tour') return TOUR_DASHBOARD;

  // Guida del Parco
  if ((path === '/area' && tenantType === 'guida') || path.startsWith('/area/parco')) {
    return GUIDA_ITINERARI;
  }

  // E-commerce
  if (path === '/area/ecommerce') return ECOMMERCE_DASHBOARD;
  if (path.startsWith('/area/ecommerce')) {
    const slug = path.replace('/area/ecommerce', '').replace(/^\//, '');
    return ECOMMERCE_GUIDES[slug] || ECOMMERCE_DASHBOARD;
  }

  return null;
}

// API principale usata da AppShell.
export function getSectionGuide({ role, tenantType, pathname }) {
  const path = normalizePath(pathname);
  if (role === 'admin') return normalize(getAdminGuide(path));
  if (role === 'staff') return normalize(STAFF_GUIDES[path] || null);
  if (role === 'stakeholder') return normalize(stakeholderGuide(tenantType, path));
  return null;
}
